
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star } from 'lucide-react';
import { format } from 'date-fns';
import { getSafeClient } from '@/integrations/supabase/safeClient';

interface Review {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  reviewer_id: string;
  reviewer_name: string;
}

interface ProfileReviewsProps {
  profileId: string;
  profileName?: string;
  reviewType?: string;
  limit?: number;
}

const ProfileReviews: React.FC<ProfileReviewsProps> = ({ 
  profileId,
  profileName, // Add optional props
  reviewType = "all",
  limit
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const supabaseClient = await getSafeClient();
        const { data, error } = await supabaseClient.rpc('get_profile_reviews', { profile_id_param: profileId });

        if (error) {
          console.error("Error fetching profile reviews:", error);
          setError("Failed to load reviews.");
        } else {
          // Apply limit if specified
          const limitedData = limit ? data?.slice(0, limit) : data;
          // Filter by review type if specified
          const filteredData = reviewType !== "all" 
            ? limitedData?.filter((review: any) => review.review_type === reviewType) 
            : limitedData;
          
          setReviews(filteredData || []);
        }
      } catch (error) {
        console.error("Unexpected error fetching profile reviews:", error);
        setError("Failed to load reviews.");
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [profileId, reviewType, limit]);

  if (loading) {
    return <p>Loading reviews...</p>;
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  if (!reviews || reviews.length === 0) {
    return <p>No reviews available for this profile.</p>;
  }

  return (
    <div>
      {profileName && <h3 className="text-lg font-medium mb-4">Reviews for {profileName}</h3>}
      {reviews.map((review) => (
        <Card key={review.id} className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center mb-2">
              <Star className="h-5 w-5 text-yellow-500 mr-1" />
              <span className="font-semibold">{review.rating}</span>
            </div>
            <p className="text-sm text-gray-500">
              By {review.reviewer_name} on {format(new Date(review.created_at), 'MMMM dd, yyyy')}
            </p>
            <p className="mt-2">{review.comment}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ProfileReviews;
